import type { RefObject } from 'react';
import type { LivePlotHandle, LivePlotImageOptions } from '../components/LivePlot';
import type { ChartImage } from '../types/chartTypes';

type ChartRefMap<K extends string> = Partial<Record<K, LivePlotHandle | null>>;

export type CollectLivePlotImagesOptions<K extends string> = {
  keys: readonly K[];
  // RefObject is enough here because callers own mutation; this helper only reads each live chart handle.
  chartRefs: RefObject<ChartRefMap<K>>;
  existingCharts?: Partial<Record<K, ChartImage>>;
  force?: boolean;
  getImageOptions?: (key: K) => LivePlotImageOptions | undefined;
};

export async function collectLivePlotImages<K extends string>({ keys, chartRefs, existingCharts, force = false, getImageOptions }: CollectLivePlotImagesOptions<K>): Promise<Partial<Record<K, ChartImage>>> {
  const charts: Partial<Record<K, ChartImage>> = { ...(existingCharts ?? {}) };

  for (const key of keys) {
    if (!force && charts[key]?.base64) continue;

    // Missing handles usually mean the plot has not mounted yet or the requested key is not rendered on screen.
    const chart = chartRefs.current[key];
    if (!chart) {
      throw new Error(`${key} is not ready for image export.`);
    }

    charts[key] = await chart.createImage(getImageOptions?.(key));
  }

  return charts;
}
